import type { Region, RegionProgress, RegionStatus } from '../types';
import { shapes } from './koreaMapShapes';

// 큰 도(道)를 먼저 그리고 그 안의 광역시를 나중에 그려야 광역시가 가려지지 않는다.
const drawOrder = ['gyeonggi', 'gangwon', 'chungnam', 'chungbuk', 'gyeongbuk', 'jeonbuk', 'jeonnam', 'gyeongnam', 'jeju', 'incheon', 'seoul', 'sejong', 'daejeon', 'daegu', 'gwangju', 'ulsan', 'busan'];
const metroIds = new Set(['seoul', 'incheon', 'sejong', 'daejeon', 'daegu', 'gwangju', 'ulsan', 'busan']);

export const statusMeta: Record<RegionStatus, { label: string; color: string }> = {
  unvisited: { label: '미방문', color: '#dde1dc' },
  wishlist: { label: '가고 싶음', color: '#f5c85b' },
  planned: { label: '여행 예정', color: '#78aee8' },
  visited: { label: '방문 완료', color: '#4e9f76' },
  revisit: { label: '재방문 희망', color: '#9c78c6' },
};

type Props = {
  regions: Region[];
  progress: RegionProgress[];
  selectedId: string;
  onSelect: (regionId: string) => void;
  compact?: boolean;
  readOnly?: boolean;
};

export function KoreaMap({ regions, progress, selectedId, onSelect, compact = false, readOnly = false }: Props) {
  const progressByRegion = new Map(progress.map((item) => [item.regionId, item]));
  return (
    <div className={`korea-map-wrap ${compact ? 'is-compact' : ''}`}>
      <svg className="korea-map" viewBox="0 0 430 650" role="img" aria-label="대한민국 광역자치단체 여행 상태 지도">
        <defs>
          <filter id="region-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#13342c" floodOpacity="0.13" />
          </filter>
        </defs>
        {[...regions].sort((a, b) => drawOrder.indexOf(a.id) - drawOrder.indexOf(b.id)).map((region) => {
          const shape = shapes[region.id];
          if (!shape) return null;
          const status = progressByRegion.get(region.id)?.status ?? 'unvisited';
          const selected = selectedId === region.id;
          return (
            <g
              key={region.id}
              className={`map-region status-${status} ${metroIds.has(region.id) ? 'is-metro' : ''} ${selected ? 'is-selected' : ''}`}
              role="button"
              tabIndex={readOnly ? -1 : 0}
              aria-label={`${region.name}, ${statusMeta[status].label}`}
              onClick={() => onSelect(region.id)}
              onKeyDown={(event) => { if (!readOnly && (event.key === 'Enter' || event.key === ' ')) onSelect(region.id); }}
            >
              <path d={shape.path} fill={statusMeta[status].color} filter={selected ? 'url(#region-shadow)' : undefined} />
              <text x={shape.x} y={shape.y} textAnchor="middle" dominantBaseline="central">{region.shortName}</text>
            </g>
          );
        })}
      </svg>
      {!compact && (
        <div className="map-legend" aria-label="지도 상태 범례">
          {(Object.entries(statusMeta) as Array<[RegionStatus, { label: string; color: string }]>).map(([status, meta]) => (
            <span key={status}><i style={{ background: meta.color }} />{meta.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}
